import React, { useRef, useState } from 'react';

// Fixed viewBox; the <svg> scales responsively via CSS width:100% while the
// coordinate math below stays in this stable unit space.
const VIEW_W = 760;
const VIEW_H = 260;
const PADDING = { top: 16, right: 16, bottom: 28, left: 34 };
const PLOT_W = VIEW_W - PADDING.left - PADDING.right;
const PLOT_H = VIEW_H - PADDING.top - PADDING.bottom;
const MAX_X_LABELS = 7;

// Status colors already used everywhere else in the app for these exact
// categories (see .status.answered/.missed/.attempted) — reused here rather
// than inventing a separate categorical palette for the same three states.
const SERIES = [
  { key: 'answered', label: 'Answered', color: 'var(--color-success)' },
  { key: 'missed', label: 'Missed', color: 'var(--color-danger)' },
  { key: 'attempted', label: 'Attempted', color: 'var(--color-warning)' },
];

// Rounds a max value up to a "clean" axis ceiling (1/2/5 x 10^n) so gridline
// labels read as round numbers instead of arbitrary fractions.
const niceMax = (value) => {
  if (value <= 4) return 4;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const residual = value / magnitude;
  const niceResidual = residual <= 1 ? 1 : residual <= 2 ? 2 : residual <= 5 ? 5 : 10;
  return niceResidual * magnitude;
};

// Catmull-Rom-to-Bezier smoothing so the line curves through each point
// instead of joining them with straight segments — points are daily
// aggregates, so a smooth curve reads as a trend rather than a jagged EKG.
const smoothPath = (coords) => {
  if (coords.length === 0) return '';
  if (coords.length === 1) return `M ${coords[0].x} ${coords[0].y}`;
  let d = `M ${coords[0].x} ${coords[0].y}`;
  for (let i = 0; i < coords.length - 1; i++) {
    const p0 = coords[i - 1] || coords[i];
    const p1 = coords[i];
    const p2 = coords[i + 1];
    const p3 = coords[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
};

function TrendChart({ points }) {
  const [view, setView] = useState('chart'); // 'chart' | 'table'
  const [hoverIndex, setHoverIndex] = useState(null);
  const svgRef = useRef(null);

  const maxCount = Math.max(1, ...points.map((p) => Math.max(p.answered, p.missed, p.attempted)));
  const yMax = niceMax(maxCount);
  const yTicks = [0, yMax * 0.25, yMax * 0.5, yMax * 0.75, yMax];

  const stepX = points.length > 1 ? PLOT_W / (points.length - 1) : 0;
  const xAt = (index) => PADDING.left + stepX * index;
  const yAt = (value) => PADDING.top + PLOT_H - (value / yMax) * PLOT_H;

  const labelEvery = Math.max(1, Math.ceil(points.length / MAX_X_LABELS));

  const seriesCoords = (key) => points.map((p, index) => ({ x: xAt(index), y: yAt(p[key]) }));

  const linePath = (key) => smoothPath(seriesCoords(key));

  // Same smoothed line, closed down to the zero baseline, for the filled
  // area under each series.
  const areaPath = (key) => {
    const coords = seriesCoords(key);
    if (coords.length === 0) return '';
    const baselineY = yAt(0);
    const first = coords[0];
    const last = coords[coords.length - 1];
    return `${smoothPath(coords)} L ${last.x} ${baselineY} L ${first.x} ${baselineY} Z`;
  };

  const handlePointerMove = (event) => {
    if (!svgRef.current || points.length === 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    const scaleX = VIEW_W / rect.width;
    const localX = (event.clientX - rect.left) * scaleX;
    const raw = Math.round((localX - PADDING.left) / (stepX || 1));
    setHoverIndex(Math.min(points.length - 1, Math.max(0, raw)));
  };

  const hovered = hoverIndex !== null ? points[hoverIndex] : null;
  const tooltipLeftPct =
    hoverIndex !== null && points.length > 1
      ? Math.min(92, Math.max(8, (hoverIndex / (points.length - 1)) * 100))
      : 50;

  if (points.length === 0) {
    return <p className="agent-page-empty">No call activity in this date range.</p>;
  }

  return (
    <div className="chart-card">
      <div className="trend-chart-header">
        <div className="trend-legend">
          {SERIES.map((series) => (
            <span className="trend-legend-item" key={series.key}>
              <svg width="16" height="4" aria-hidden="true">
                <line x1="0" y1="2" x2="16" y2="2" stroke={series.color} strokeWidth="2" strokeLinecap="round" />
              </svg>
              {series.label}
            </span>
          ))}
        </div>
        <button
          type="button"
          className="btn btn-secondary trend-view-toggle"
          onClick={() => setView(view === 'chart' ? 'table' : 'chart')}
        >
          {view === 'chart' ? 'View as table' : 'View as chart'}
        </button>
      </div>

      {view === 'table' ? (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Answered</th>
                <th>Missed</th>
                <th>Attempted</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {points.map((p) => (
                <tr key={p.date}>
                  <td>{p.label}</td>
                  <td>{p.answered}</td>
                  <td>{p.missed}</td>
                  <td>{p.attempted}</td>
                  <td>{p.answered + p.missed + p.attempted}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="trend-chart-svg-wrap">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
            className="trend-chart-svg"
            onPointerMove={handlePointerMove}
            onPointerLeave={() => setHoverIndex(null)}
            role="img"
            aria-label="Daily call volume by status"
          >
            <defs>
              {SERIES.map((series) => (
                <linearGradient
                  key={series.key}
                  id={`trend-area-${series.key}`}
                  x1="0"
                  y1={PADDING.top}
                  x2="0"
                  y2={yAt(0)}
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0%" stopColor={series.color} stopOpacity="0.35" />
                  <stop offset="100%" stopColor={series.color} stopOpacity="0.02" />
                </linearGradient>
              ))}
            </defs>

            {yTicks.map((tick) => (
              <g key={tick}>
                <line
                  x1={PADDING.left}
                  x2={VIEW_W - PADDING.right}
                  y1={yAt(tick)}
                  y2={yAt(tick)}
                  className="trend-gridline"
                />
                <text x={PADDING.left - 8} y={yAt(tick)} className="trend-axis-label" textAnchor="end" dy="0.32em">
                  {Math.round(tick)}
                </text>
              </g>
            ))}

            {points.map((p, index) =>
              index % labelEvery === 0 || index === points.length - 1 ? (
                <text
                  key={p.date}
                  x={xAt(index)}
                  y={VIEW_H - PADDING.bottom + 18}
                  className="trend-axis-label"
                  textAnchor="middle"
                >
                  {p.label}
                </text>
              ) : null
            )}

            {SERIES.map((series) => (
              <path key={series.key} d={areaPath(series.key)} fill={`url(#trend-area-${series.key})`} stroke="none" />
            ))}

            {SERIES.map((series) => (
              <path
                key={series.key}
                d={linePath(series.key)}
                fill="none"
                stroke={series.color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}

            {hovered && (
              <>
                <line
                  x1={xAt(hoverIndex)}
                  x2={xAt(hoverIndex)}
                  y1={PADDING.top}
                  y2={VIEW_H - PADDING.bottom}
                  className="trend-crosshair"
                />
                {SERIES.map((series) => (
                  <g key={series.key}>
                    <circle cx={xAt(hoverIndex)} cy={yAt(hovered[series.key])} r="6" fill="var(--color-surface)" />
                    <circle cx={xAt(hoverIndex)} cy={yAt(hovered[series.key])} r="4" fill={series.color} />
                  </g>
                ))}
              </>
            )}
          </svg>

          {hovered && (
            <div className="trend-tooltip" style={{ left: `${tooltipLeftPct}%` }}>
              <div className="trend-tooltip-date">{hovered.label}</div>
              {SERIES.map((series) => (
                <div className="trend-tooltip-row" key={series.key}>
                  <span className="trend-tooltip-key">
                    <svg width="12" height="4" aria-hidden="true">
                      <line x1="0" y1="2" x2="12" y2="2" stroke={series.color} strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {series.label}
                  </span>
                  <span className="trend-tooltip-value">{hovered[series.key]}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TrendChart;
